/**
 * Chapter010.js — Roles & Responsibilities (OHSE 010)
 * Migrated from SWMS Platform Chapter010.tsx → JavaScript
 *
 * Props:
 *   roles                   {array}  roles_responsibilities from swmsData
 *   onRolesChange           {fn}     (newRoles) => void
 *   employeeResponsibilities {array} employee_responsibilities from swmsData
 *   onEmployeeChange        {fn}     (newList) => void
 *   companyName             {string} from companyInfo.name
 */

import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';
const textareaCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all resize-y min-h-[52px]';

function emptyRole() {
  return {
    id: crypto.randomUUID(),
    role_title: '',
    person_name: '',
    duties: [''],
    signature: '',
    date: '',
  };
}

export default function Chapter010({ roles = [], onRolesChange, employeeResponsibilities = [], onEmployeeChange, companyName }) {
  const cn = companyName || '[Company Name]';

  // ── Role handlers ──
  const addRole = useCallback(() => onRolesChange([...roles, emptyRole()]), [roles, onRolesChange]);
  const removeRole = useCallback((id) => onRolesChange(roles.filter(r => r.id !== id)), [roles, onRolesChange]);
  const updateRole = useCallback((id, field, value) => {
    onRolesChange(roles.map(r => r.id === id ? { ...r, [field]: value } : r));
  }, [roles, onRolesChange]);

  // ── Employee responsibility handlers ──
  const addDuty = useCallback(() => onEmployeeChange([...employeeResponsibilities, { id: crypto.randomUUID(), duty: '' }]), [employeeResponsibilities, onEmployeeChange]);
  const removeDuty = useCallback((id) => onEmployeeChange(employeeResponsibilities.filter(e => e.id !== id)), [employeeResponsibilities, onEmployeeChange]);
  const updateDuty = useCallback((id, value) => {
    onEmployeeChange(employeeResponsibilities.map(e => e.id === id ? { ...e, duty: value } : e));
  }, [employeeResponsibilities, onEmployeeChange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 010</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">Roles &amp; Responsibilities</h2>
      </div>

      {/* Declaration */}
      <div className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm text-[#1a1a2e] leading-relaxed space-y-2">
        <p>
          <span className="font-semibold text-[#c9a84c]">{cn}</span> provides the following key
          trained and competent personnel on site.
        </p>
        <p>
          The roles and responsibilities of employees within{' '}
          <span className="font-semibold text-[#c9a84c]">{cn}</span> regarding OHSE are below.
        </p>
      </div>

      {/* Role Cards */}
      {roles.map((role, idx) => (
        <RoleCard key={role.id} role={role} index={idx}
          onUpdate={(field, value) => updateRole(role.id, field, value)}
          onRemove={() => removeRole(role.id)} />
      ))}

      {roles.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 text-center border border-[#e0dbd0] rounded-lg bg-white">
          <p className="text-sm text-gray-400 mb-3">No roles defined yet.</p>
          <button type="button" onClick={addRole}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-[#1a1a2e] rounded hover:bg-[#252547] transition-colors">
            <Plus className="w-4 h-4" /> Add Role
          </button>
        </div>
      )}

      {roles.length > 0 && (
        <div className="flex justify-center">
          <button type="button" onClick={addRole}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
            <Plus className="w-4 h-4" /> Add Role
          </button>
        </div>
      )}

      {/* Employee Responsibilities */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">Employees</h3>
          <p className="text-xs text-gray-500 mt-0.5">General responsibilities applicable to all employees on site</p>
        </div>
        <div className="p-4 space-y-2">
          <p className="text-sm text-gray-500 mb-3">All employees are responsible for the following:</p>

          {employeeResponsibilities.map((item, idx) => (
            <div key={item.id} className="flex items-start gap-2 group">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#1a1a2e]/10 text-[#1a1a2e] text-[10px] font-semibold shrink-0 mt-1.5">
                {idx + 1}
              </span>
              <textarea value={item.duty} placeholder="Describe employee responsibility..."
                onChange={e => updateDuty(item.id, e.target.value)}
                className={textareaCls + ' flex-1'} />
              <button type="button" onClick={() => removeDuty(item.id)}
                className="p-1 text-red-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100 mt-1">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}

          <div className="flex justify-center pt-2">
            <button type="button" onClick={addDuty}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
              <Plus className="w-4 h-4" /> Add Employee Responsibility
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RoleCard({ role, index, onUpdate, onRemove }) {
  const addRoleDuty = () => onUpdate('duties', [...role.duties, '']);
  const removeRoleDuty = (i) => onUpdate('duties', role.duties.filter((_, di) => di !== i));
  const updateRoleDuty = (i, value) => {
    const next = [...role.duties];
    next[i] = value;
    onUpdate('duties', next);
  };

  return (
    <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 bg-[#f0ede6] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#1a1a2e] text-white text-xs font-semibold shrink-0">
            {index + 1}
          </span>
          <span className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">
            {role.role_title || 'Unnamed Role'}
          </span>
        </div>
        <button type="button" onClick={onRemove} className="p-1 text-red-400 hover:text-red-600 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Role Title">
            <input type="text" value={role.role_title} placeholder="e.g. Works Manager, OHSE Coordinator"
              onChange={e => onUpdate('role_title', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Person Name">
            <input type="text" value={role.person_name} placeholder="e.g. John Smith"
              onChange={e => onUpdate('person_name', e.target.value)} className={inputCls} />
          </Field>
        </div>

        {role.person_name && (
          <p className="text-sm text-gray-500">
            <span className="font-semibold text-[#c9a84c]">{role.person_name}</span> is responsible
            for OHSE at the workplace and duties include:
          </p>
        )}

        {/* Duties */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Duties &amp; Responsibilities</p>
          <div className="space-y-1.5">
            {role.duties.map((duty, i) => (
              <div key={i} className="flex items-start gap-2 group">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#1a1a2e]/10 text-[#1a1a2e] text-[10px] font-semibold shrink-0 mt-1.5">
                  {i + 1}
                </span>
                <textarea value={duty} placeholder="Describe duty..."
                  onChange={e => updateRoleDuty(i, e.target.value)}
                  className={textareaCls + ' flex-1'} />
                <button type="button" onClick={() => removeRoleDuty(i)}
                  className="p-1 text-red-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100 mt-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex justify-start mt-2">
            <button type="button" onClick={addRoleDuty}
              className="inline-flex items-center gap-1 text-xs text-[#c9a84c] hover:text-[#c9a84c]/80 font-semibold uppercase tracking-wider transition-colors">
              <Plus className="w-3.5 h-3.5" /> Add Duty
            </button>
          </div>
        </div>

        {/* Signature & Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-[#e0dbd0]">
          <Field label="Signature (type name)">
            <input type="text" value={role.signature} placeholder="Signature"
              onChange={e => onUpdate('signature', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Date">
            <input type="date" value={role.date}
              onChange={e => onUpdate('date', e.target.value)} className={inputCls} />
          </Field>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
